import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import './App.css';
import { Helmet } from 'react-helmet';
import { odeleLightTheme } from './theme';
import { ChakraProvider } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import NewsletterLayout from './components/Layouts/NewsletterLayout';
import DiscoveryPage from './routes/Discovery';
import WriterLayout from './components/Layouts/WriterLayout';
import CreateArticle from './routes/CreateArticle';
import PageNotFound from './routes/NotFound';
import SearchResult from './routes/SearchResult';
import ManageArticle from './routes/ManageArticles';
import Profile from './routes/Profile';
import RecommendTopicsPage from './routes/RecommendTopics';
import EditArticle from './routes/EditArticle';
import BreakingNewsPage from './routes/BreakingNews';
import RelatedArticlesPage from './routes/RelatedArticles';
import FeaturedVoicesPage from './routes/FeaturedVoices';
import CreateRequest from './routes/CreateRequest';
import DetailRequest from './routes/DetailRequest';
import { checkAuth } from './components/Auth/checkAuth';
import { checkCreator } from './components/Auth/checkCreator';
import ReviewSubmissions from './routes/ReviewSubmissions';
import ArticleViewer from './routes/ArticleViewer';
import EditRequest from './routes/EditRequest';
import AccountSettings from './routes/AccountSettings';
import TwoFactorAuthentication from './routes/TwoFactorAuthentication';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';
import CookiePolicy from './components/CookiePolicy';
import AllNotifications from './routes/AllNotifications';
import ErrorBoundary from './components/ErrorBoundary';
import Roadmap from './components/Roadmap';
import Learning from './routes/Learning';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

const router = createBrowserRouter([
  {
    element: <NewsletterLayout />,
    children: [
      { path: '/', element: <DiscoveryPage /> },
      { path: '/recommended-topics', element: <RecommendTopicsPage /> },
      { path: '/breaking-news', element: <BreakingNewsPage /> },
      { path: '/related-articles', element: <RelatedArticlesPage /> },
      { path: '/featured-voices', element: <FeaturedVoicesPage /> },
      { path: '/search', element: <SearchResult /> },
      { path: '/:username', element: <Profile /> },
      { path: '/articles/:articleId', element: <ArticleViewer /> },
      { path: '/privacy-policy', element: <PrivacyPolicy /> },
      { path: '/terms-of-use', element: <TermsOfUse /> },
      { path: '/cookie-policy', element: <CookiePolicy /> },
      { path: '/roadmap', element: <Roadmap /> },
      { path: '/learning', element: <Learning />, loader: checkAuth },
      { path: '/notifications', element: <AllNotifications />, loader: checkAuth }
    ]
  },
  {
    element: <WriterLayout />,
    children: [
      { path: '/account-settings', element: <AccountSettings />, loader: checkAuth },
      {
        path: '/account-settings/two-factor-authentication',
        element: <TwoFactorAuthentication />,
        loader: checkAuth
      }
    ]
  },
  {
    element: <WriterLayout />,
    children: [
      { path: '/article/create', element: <CreateArticle />, loader: checkCreator },
      { path: '/article/edit', element: <EditArticle />, loader: checkCreator },
      { path: '/article/manage', element: <ManageArticle />, loader: checkAuth }
    ]
  },
  {
    element: <WriterLayout />,
    children: [
      { path: '/request/create', element: <CreateRequest />, loader: checkCreator },
      { path: '/request/:id/edit', element: <EditRequest />, loader: checkCreator },
      { path: '/request/:id/submissions', element: <ReviewSubmissions />, loader: checkAuth }
    ]
  },
  {
    element: <NewsletterLayout />,
    children: [{ path: '/request/:id/detail', element: <DetailRequest /> }]
  },
  { path: '*', element: <PageNotFound /> }
]);

function App() {
  return (
    <>
      <ErrorBoundary>
        <Helmet>
          <link rel="icon" href="/images/favicon.png" />
          <meta http-equiv="Accept-CH" content="DPR, Viewport-Width, Width" />
        </Helmet>

        <QueryClientProvider client={queryClient}>
          <ChakraProvider theme={odeleLightTheme}>
            <RouterProvider router={router} />
          </ChakraProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </>
  );
}

export default App;
